import { deleteField } from "firebase/firestore";

import type { FirePost, PostImage } from "../types/models";
import { getPostImageUrls } from "./postImages";
import type { UploadedPostImage } from "./postAttachments";

export const POST_EDIT_MODERATION_STATUS = "pending" as const;

export type PostEditImageState = {
  images: (PostImage | UploadedPostImage)[];
  legacyImageUrls: string[];
};

export type PostEditPdfState = {
  attachmentUrl: string;
  attachmentStoragePath?: string;
  attachmentName?: string;
};

export function buildPostEditPdfStoragePath(
  uid: string,
  postId: string,
  editId = Date.now()
) {
  return `moderation-pending/${uid}/${postId}/edit_${editId}.pdf`;
}

export function buildPostEditPayload(params: {
  text: string;
  imageEdit: PostEditImageState;
  pdf: PostEditPdfState | null;
}) {
  const updatePayload: Record<string, any> = {
    text: params.text,
    moderationStatus: POST_EDIT_MODERATION_STATUS,
    images: params.imageEdit.images.length > 0 ? params.imageEdit.images : deleteField(),
    imageUrls:
      params.imageEdit.legacyImageUrls.length > 0
        ? params.imageEdit.legacyImageUrls
        : deleteField(),
  };

  if (params.pdf) {
    updatePayload.attachmentType = "pdf";
    updatePayload.attachmentUrl = params.pdf.attachmentUrl;
    updatePayload.attachmentStoragePath = params.pdf.attachmentStoragePath
      ? params.pdf.attachmentStoragePath
      : deleteField();
    updatePayload.attachmentName = params.pdf.attachmentName ?? "";
    return updatePayload;
  }

  updatePayload.attachmentType =
    params.imageEdit.images.length > 0 || params.imageEdit.legacyImageUrls.length > 0
      ? "image"
      : deleteField();
  updatePayload.attachmentUrl = deleteField();
  updatePayload.attachmentStoragePath = deleteField();
  updatePayload.attachmentName = deleteField();
  return updatePayload;
}

export function postEditHasChanges(params: {
  post: Partial<FirePost>;
  text: string;
  retainedImageUrls: string[];
  newImageCount: number;
  pdfUrl: string;
  pdfName: string;
  hasNewPdf: boolean;
}) {
  const previousText = String(params.post.text ?? "").trim();
  const previousImageUrls = getPostImageUrls(params.post);
  const hasExistingPdf =
    params.post.attachmentType === "pdf" && String(params.post.attachmentUrl ?? "").length > 0;
  const hadLegacyImageAttachment =
    params.post.attachmentType === "image" && String(params.post.attachmentUrl ?? "").length > 0;
  const imageListUnchanged =
    previousImageUrls.length === params.retainedImageUrls.length &&
    previousImageUrls.every((url, index) => params.retainedImageUrls[index] === url) &&
    params.newImageCount === 0;
  const pdfUnchanged =
    (!hasExistingPdf && !params.pdfUrl && !params.hasNewPdf) ||
    (hasExistingPdf &&
      params.pdfUrl === String(params.post.attachmentUrl ?? "") &&
      params.pdfName === String(params.post.attachmentName ?? "") &&
      !params.hasNewPdf);

  return !(
    previousText === params.text.trim() &&
    imageListUnchanged &&
    pdfUnchanged &&
    !hadLegacyImageAttachment
  );
}
